import { useDisclosure } from '@withjoy/joykit';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';
import { Guests, Help, Invite, Profile, Settings, LogOut, Tip, NotificationBubble } from '@withjoy/joykit/icons';
import { monorepoRoutePaths } from '@apps/admin/route.paths';
import { NavButtonAvatar } from '@apps/admin/routes/Dashboard/components/AdminMenu/AdminMenu.styles';
import { AdminMenuProps } from '@apps/admin/routes/Dashboard/components/AdminMenu/AdminMenu';
import { useRouterHelper } from '@shared/core';
import { useEffect } from 'react';
import { useAdminMenuHealth } from '@apps/admin/routes/Dashboard/components/AdminMenu/AdminMenu.health';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useAuth } from '@shared/components/AuthProvider';
import { useInviteAdminDialogContext } from '@shared/context/InviteAdminDialog/InviteAdminDialog.provider';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useFeedBackSurvey } from '@shared/utils/feedbackSurvey';
import { useIntercom } from '@shared/core/intercom';

export const useAdminMenuController = ({ adminData: data, error }: AdminMenuProps) => {
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  const { isOpen, onToggle } = useDisclosure();
  const { onLoadComplete, onLoadFailure } = useAdminMenuHealth();
  const { provideLogout } = useAuth();

  const menuRef = useOuterClick(onToggle, isOpen);

  const eventDisplayName = data?.eventByName?.info.eventDisplayName ?? '';
  const photoAltTag = data?.eventByName?.photo?.altTag ?? 'Event Photo';
  const photoUrl = data?.eventByName?.photo?.url ?? '';
  const photosPath = routeHelpers.buildPath(monorepoRoutePaths.photos);
  const userAvatar = data?.me?.avatar?.url ?? '';
  const { openInviteAdminDialog } = useInviteAdminDialogContext();
  const startSurvey = useFeedBackSurvey();
  const { value } = useFeatureValue('dashboardFeedbackSurveyEnabled');
  const isDashboardFeedbackSurveyEnabled = value === 'on';
  const intercom = useIntercom();

  const menuOptions = [
    {
      title: 'Event Settings',
      icon: Settings,
      path: routeHelpers.buildPath(monorepoRoutePaths.settings),
      onClick: () => telemetry.openPage({ label: 'settings', section: 'Avatar' })
    },
    {
      title: 'Invite Collaborator',
      icon: Invite,
      onClick: () => {
        telemetry.openPage({ label: 'inviteAdmin', section: 'Avatar' });
        openInviteAdminDialog();
      }
    },
    ...(isDashboardFeedbackSurveyEnabled
      ? [
          {
            title: 'Leave a Suggestion',
            icon: Tip,
            onClick: () => {
              telemetry.openPage({ label: 'leaveSuggestion', section: 'Avatar' });
              startSurvey();
              onToggle();
            },
            hasDivider: true
          }
        ]
      : []),
    {
      title: 'Contact Support',
      icon: NotificationBubble,
      onClick: () => {
        telemetry.openPage({ label: 'contactSupport', section: 'Avatar' });
        intercom.show();
        onToggle();
      }
    },
    {
      title: 'Help',
      icon: Help,
      path: 'https://help.withjoy.com/',
      onClick: () => telemetry.openPage({ label: 'help', section: 'Avatar' })
    },
    {
      title: 'Manage Events',
      icon: Guests,
      path: '/account/events',
      onClick: () => telemetry.openPage({ label: 'myEvents', section: 'Avatar' }),
      hasDivider: true
    },
    {
      title: 'Account Info',
      icon: !!userAvatar ? () => NavButtonAvatar({ src: userAvatar, alt: 'User Avatar Icon' }) : Profile,
      path: `${window.location.origin}/${monorepoRoutePaths.accountPersonalInfo}`,
      onClick: () => telemetry.openPage({ label: 'account', section: 'Avatar' })
    },
    {
      title: 'Sign Out',
      icon: LogOut,
      path: '#',
      onClick: () => {
        telemetry.openPage({ label: 'accountSignOut', section: 'Avatar' });
        provideLogout({});
      }
    }
  ];

  useEffect(() => {
    if (data) {
      onLoadComplete();
    }
    if (error) {
      onLoadFailure('GetEventForAdminMenuQuery failed', error);
    }
  }, [data, error, onLoadComplete, onLoadFailure]);

  return {
    menuRef,
    isOpen,
    onToggle,
    photoUrl,
    photoAltTag,
    eventDisplayName,
    menuOptions,
    photosPath,
    telemetry
  };
};
